import { z } from "zod";
import { paymentRequirementSchema } from "./shared";

export const serviceOptionSchema = z.object({
  id: z.string().min(1).optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  duration: z.number().int().nonnegative(),
  price: z.number().nonnegative(),
  referenceId: z.string().min(1),
  optionId: z.string().optional(),
});

export const serviceSchema = z.object({
  id: z.string().min(1),
  userId: z.string(),
  salonId: z.string(),
  categoryId: z.string().nullable().optional(),
  referenceId: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  price: z.number(),
  duration: z.number(),
  sortOrder: z.number().nullable().optional(),
  hasPayAdvance: z.boolean().optional(),
  hasConsultation: z.boolean().optional(),
  paymentRequirement: paymentRequirementSchema.optional(),
  depositAmount: z.number().nullable().optional(),
  up: z.boolean().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
  options: z.array(
    z.object({
      id: z.string(),
      serviceId: z.string(),
      optionId: z.string().nullable().optional(),
      title: z.string(),
      description: z.string().nullable().optional(),
      price: z.number(),
      duration: z.number(),
      referenceId: z.string().nullable().optional(),
      createdAt: z.union([z.string(), z.date()]),
      updatedAt: z.union([z.string(), z.date()]),
    }),
  ),
});

export const listServicesQuerySchema = z.object({
  salonId: z.string().min(1),
});

export const listServicesResponseSchema = z.object({
  services: z.array(serviceSchema),
});

export const serviceIdParamsSchema = z.object({
  serviceId: z.string().min(1),
});

export const serviceDetailResponseSchema = z.object({
  service: serviceSchema,
});

export const createServiceSchema = z.object({
  userId: z.string().min(1),
  salonId: z.string().min(1),
  teamMemberId: z.string().min(1).optional(),
  categoryId: z.string().min(1).optional().nullable(),
  title: z.string().min(1),
  description: z.string().optional(),
  duration: z.number().int().positive(),
  price: z.number().nonnegative(),
  referenceId: z.string().min(1),
  up: z.boolean().optional(),
  hasPayAdvance: z.boolean().optional(),
  hasConsultation: z.boolean().optional(),
  paymentRequirement: paymentRequirementSchema.optional(),
  depositAmount: z.number().nonnegative().optional().nullable(),
  sortOrder: z.number().int().optional(),
  options: z.array(serviceOptionSchema).optional(),
});

export const createServiceResponseSchema = z.object({
  serviceId: z.string(),
});

export const publicServiceOptionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z
    .string()
    .optional()
    .nullable()
    .transform((value) => value ?? ""),
  duration: z.number().optional().default(0),
  price: z.number().optional().default(0),
  referenceId: z.string().optional().nullable(),
  optionId: z.string().optional().nullable(),
});

export const publicServiceSchema = z.object({
  id: z.string(),
  salonId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  categoryId: z.string().optional().nullable(),
  referenceId: z.string().optional().nullable(),
  title: z.string(),
  description: z
    .string()
    .optional()
    .nullable()
    .transform((value) => value ?? ""),
  price: z.number().optional().default(0),
  duration: z.number().optional().default(0),
  up: z.boolean().optional().default(false),
  hasPayAdvance: z.boolean().optional().default(false),
  hasConsultation: z.boolean().optional().default(false),
  paymentRequirement: z
    .enum(["NONE", "DEPOSIT", "FULL"])
    .optional()
    .default("NONE"),
  depositAmount: z.number().nullable().optional().default(null),
  /**
   * Customers may book this service only at least this many minutes before
   * it starts; null means no notice. A member clone carries its business
   * service's value. The API leaves the starts that are too soon out of
   * openings and refuses them on commit; clients show
   * `booking.service.minNoticeNote`.
   */
  minBookingNoticeMinutes: z.number().int().positive().nullable().optional(),
  options: z.array(publicServiceOptionSchema).optional().default([]),
});

export const publicServicesListResponseSchema = z.object({
  services: z.array(publicServiceSchema),
});

export const publicServiceDetailResponseSchema = z.object({
  service: publicServiceSchema,
});

export type ServiceOption = z.infer<typeof serviceOptionSchema>;
export type Service = z.infer<typeof serviceSchema>;
export type ListServicesQuery = z.infer<typeof listServicesQuerySchema>;
export type ListServicesResponse = z.infer<typeof listServicesResponseSchema>;
export type ServiceIdParams = z.infer<typeof serviceIdParamsSchema>;
export type ServiceDetailResponse = z.infer<typeof serviceDetailResponseSchema>;
export type CreateServiceInput = z.infer<typeof createServiceSchema>;
export type CreateServiceResponse = z.infer<typeof createServiceResponseSchema>;
export type PublicServiceOption = z.infer<typeof publicServiceOptionSchema>;
export type PublicService = z.infer<typeof publicServiceSchema>;
export type PublicServicesListResponse = z.infer<
  typeof publicServicesListResponseSchema
>;
export type PublicServiceDetailResponse = z.infer<
  typeof publicServiceDetailResponseSchema
>;
